import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom'

import AddItemForm from 'containers/items/forms/addItemForm'
import DeleteItemForm from 'containers/items/forms/deleteItemForm'
import Items from 'containers/items/items'

import Categories from 'containers/categories/categories'
import AddCategoryForm from 'containers/categories/forms/addCategoryForm'
import DeleteCategoryForm from 'containers/categories/forms/deleteCategoryForm'

import AddRecipeCategoryForm from 'containers/recipeCategories/forms/addRecipeCategoryForm'
import DeleteRecipeCategoryForm from 'containers/recipeCategories/forms/deleteRecipeCategoryForm'
import RecipeCategories from 'containers/recipeCategories/recipeCategories'

import EditList from 'containers/lists/editList'
import AddFromMenuForm from 'containers/lists/forms/addFromMenuForm'
import AddFromRecipeForm from 'containers/lists/forms/addFromRecipeForm'
import AddListForm from 'containers/lists/forms/addListForm'
import DeleteListForm from 'containers/lists/forms/deleteListForm'
import Lists from 'containers/lists/lists'

import EditRecipe from 'containers/recipes/editRecipe'
import AddRecipeForm from 'containers/recipes/forms/addRecipeForm'
import DeleteRecipeForm from 'containers/recipes/forms/deleteRecipeForm'
import Recipes from 'containers/recipes/recipes'

import EditMenu from 'containers/menus/editMenu'
import AddMenuForm from 'containers/menus/forms/addMenuForm'
import DeleteMenuForm from 'containers/menus/forms/deleteMenuForm'
import Menus from 'containers/menus/menus'

import { FullScreenPage } from 'components/FullScreenPage'
import { Account } from 'containers/account'
import { AddAdditionalUserForm } from 'containers/account/modules/additionalUsers/forms/addAdditionalUserForm'
import { RemoveAdditionalUserForm } from 'containers/account/modules/additionalUsers/forms/removeAdditionalUserForm'
import { ChangeEmailForm } from 'containers/account/modules/changeEmail/forms/changeEmailForm'
import { ChangePasswordForm } from 'containers/account/modules/changePassword/forms/changePasswordForm'
import { DeleteAccountForm } from 'containers/account/modules/deleteAccount/forms/deleteAccountForm'
import { AppOauth } from 'containers/auth/appOauth'
import EditCategoryDetailsForm from 'containers/categories/forms/editCategoryDetailsForm'
import EditItemDetailsForm from 'containers/items/forms/editItemDetailsForm'
import EditListDetailsForm from 'containers/lists/forms/editListDetailsForm'
import { UpdateListItemForm } from 'containers/lists/forms/updateListItemForm'
import AddRecipeToMenuForm from 'containers/menus/forms/addRecipeToMenuForm'
import EditMenuDetailsForm from 'containers/menus/forms/editMenuDetailsForm'
import { RandomRecipesForm } from 'containers/menus/forms/randomRecipesForm'
import EditRecipeCategoryDetailsForm from 'containers/recipeCategories/forms/editRecipeCategoryDetailsForm'
import { Filters } from 'containers/recipes/filters'
import AcceptSharedRecipeForm from 'containers/recipes/forms/acceptSharedRecipeForm'
import { ConfirmImportedRecipeForm } from 'containers/recipes/forms/confirmImportedRecipeForm'
import CreateShareRecipeRequestForm from 'containers/recipes/forms/createShareRecipeRequestForm'
import DuplicateRecipeForm from 'containers/recipes/forms/duplicateRecipeForm'
import { EditRecipeMethod } from 'containers/recipes/forms/editMethod'
import EditRecipeDetailsForm from 'containers/recipes/forms/editRecipeDetailsForm'
import { EditRecipeIngredients } from 'containers/recipes/forms/editRecipeIngredients'
import { RemoveRecipeImageForm } from 'containers/recipes/forms/removeRecipeImageForm'
import { UpdateRecipeItemQuantityForm } from 'containers/recipes/forms/updateRecipeItemQuantityForm'
import { UploadRecipeImageForm } from 'containers/recipes/forms/uploadRecipeImageForm'
import { ItemImageModal } from 'containers/shop/itemImageModal'
import Shop from 'containers/shop/shop'
import { usePrefetchAppCriticalData } from 'utils/hooks'

function RedirectToRefAfterLogin() {
    const [searchParams] = useSearchParams()
    const ref = searchParams.get('ref')

    if (ref) {
        return <Navigate to={ref} replace />
    }

    return <Navigate to='/recipes' replace />
}

function UserRouter() {
    usePrefetchAppCriticalData()

    return (
        <Routes>
            <Route path='/login' element={<RedirectToRefAfterLogin />} />
            <Route path='/register' element={<RedirectToRefAfterLogin />} />
            <Route path='/' element={<Navigate to='/recipes' replace />} />
            <Route path='/items' element={<Items />}>
                <Route path='new' element={<AddItemForm />} />
                <Route path='delete/:itemId' element={<DeleteItemForm />} />
                <Route path='edit/:itemId' element={<EditItemDetailsForm />} />
            </Route>
            <Route path='/categories' element={<Categories />}>
                <Route path='new' element={<AddCategoryForm />} />
                <Route path='delete/:categoryId' element={<DeleteCategoryForm />} />
                <Route path='edit/:categoryId' element={<EditCategoryDetailsForm />} />
            </Route>
            <Route path='/recipe-categories' element={<RecipeCategories />}>
                <Route path='new' element={<AddRecipeCategoryForm />} />
                <Route path='delete/:recipeCategoryId' element={<DeleteRecipeCategoryForm />} />
                <Route path='edit/:recipeCategoryId' element={<EditRecipeCategoryDetailsForm />} />
            </Route>
            <Route path='/lists' element={<Lists />}>
                <Route path='new' element={<AddListForm />} />
                <Route path='delete/:listId' element={<DeleteListForm />} />
            </Route>
            <Route path='/lists/edit/:listId' element={<EditList />}>
                <Route path='details' element={<EditListDetailsForm />} />
                <Route path='add-from-recipe' element={<AddFromRecipeForm />} />
                <Route path='add-from-menu' element={<AddFromMenuForm />} />
                <Route path='update-item-quantity/:itemId' element={<UpdateListItemForm />} />
            </Route>
            <Route path='/recipes' element={<Recipes />}>
                <Route path='new' element={<AddRecipeForm />} />
                <Route path='filters' element={<Filters />} />
                <Route path='accept-shared/:shareRequestId' element={<AcceptSharedRecipeForm />} />
                <Route path='confirm-import/:importedRecipeId' element={<ConfirmImportedRecipeForm />} />
            </Route>
            <Route path='/recipes/edit/:recipeId' element={<EditRecipe />}>
                <Route path='details' element={<EditRecipeDetailsForm />} />
                <Route path='ingredients' element={<EditRecipeIngredients />} />
                <Route path='method' element={<EditRecipeMethod />} />
                <Route path='duplicate' element={<DuplicateRecipeForm />} />
                <Route path='share' element={<CreateShareRecipeRequestForm />} />
                <Route path='delete' element={<DeleteRecipeForm />} />
                <Route path='upload-image' element={<UploadRecipeImageForm />} />
                <Route path='remove-image' element={<RemoveRecipeImageForm />} />
                <Route path='update-item-quantity/:recipeItemId' element={<UpdateRecipeItemQuantityForm />} />
            </Route>
            <Route path='/menus' element={<Menus />}>
                <Route path='new' element={<AddMenuForm />} />
                <Route path='delete/:menuId' element={<DeleteMenuForm />} />
            </Route>
            <Route path='/menus/edit/:menuId' element={<EditMenu />}>
                <Route path='add-recipe' element={<AddRecipeToMenuForm />} />
                <Route path='details' element={<EditMenuDetailsForm />} />
                <Route path='random-recipes' element={<RandomRecipesForm />} />
            </Route>
            <Route path='/shop/:listId' element={<Shop />}>
                <Route path='item/:itemId/view-image' element={<ItemImageModal />} />
            </Route>
            <Route path='/account' element={<Account />}>
                <Route path='additional-user/add' element={<AddAdditionalUserForm />} />
                <Route path='additional-user/remove' element={<RemoveAdditionalUserForm />} />
                <Route path='change-email' element={<ChangeEmailForm />} />
                <Route path='change-password' element={<ChangePasswordForm />} />
                <Route path='delete' element={<DeleteAccountForm />} />
            </Route>
            <Route path='/app-oauth' element={<AppOauth />} />

            <Route
                path='*'
                element={
                    <FullScreenPage>
                        <div className='h-full flex justify-center items-center'>No route</div>
                    </FullScreenPage>
                }
            />
        </Routes>
    )
}

export default UserRouter
